import { Link, useRouterState } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";

import { useHeaderOffset } from "@/hooks/use-header-offset";
import { scrollToHash } from "@/lib/smooth-scroll";
import { useActiveSection } from "@/hooks/use-active-section";
import { useSession } from "@/hooks/use-session";

const SECTION_IDS = ["who", "ways", "programs", "how"];

const NAV = [
  { label: "Who we are", to: "/", hash: "who" },
  { label: "Ways to help", to: "/", hash: "ways" },
  { label: "Programs", to: "/", hash: "programs" },
  { label: "How it works", to: "/", hash: "how" },
];

export function SiteHeader() {
  const ref = useHeaderOffset<HTMLDivElement>();
  const { user } = useSession();
  const [open, setOpen] = useState(false);
  const href = useRouterState({ select: (s) => s.location.href });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeSection = useActiveSection(SECTION_IDS, pathname === "/");

  // Any navigation (including hash-only) collapses the mobile panel so the
  // target section is visible right away.
  useEffect(() => {
    setOpen(false);
  }, [href]);


  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[oklch(0.14_0.035_230)]/95 backdrop-blur">
      <div
        ref={ref}
        className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 py-3"
      >

        <Link to="/" className="flex items-center gap-3">
          <img src="/t1dw-logo-white.png" alt="T1D Warriors" className="h-9 w-auto" />
          <span className="hidden border-l border-cyan/50 pl-3 text-sm font-semibold tracking-wide text-cyan sm:inline">
            Volunteer
          </span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {NAV.map((item) => (
            <Link
              key={item.hash}
              to={item.to}
              hash={item.hash}
              onClick={() => {
                if (window.location.pathname === item.to) scrollToHash(item.hash);
              }}
              data-active={activeSection === item.hash ? "true" : undefined}
              aria-current={activeSection === item.hash ? "location" : false}
              className={`relative text-sm font-semibold uppercase tracking-wide transition-colors hover:text-cyan ${
                activeSection === item.hash ? "text-cyan" : "text-ice/90"
              }`}
            >
              {item.label}
              <span
                aria-hidden="true"
                className={`absolute -bottom-1.5 left-0 h-0.5 w-full origin-left bg-cyan transition-transform duration-200 ${
                  activeSection === item.hash ? "scale-x-100" : "scale-x-0"
                }`}
              />
            </Link>
          ))}
          <Link
            to="/events"
            className="text-sm font-semibold uppercase tracking-wide text-ice/90 transition-colors hover:text-cyan"
          >
            Events
          </Link>
          <a
            href="https://t1dw.org"
            target="_blank"
            rel="noreferrer"
            className="text-sm font-semibold uppercase tracking-wide text-ice/90 transition-colors hover:text-cyan"
          >
            t1dw.org
          </a>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <Link
              to="/dashboard"
              className="rounded-sm bg-cyan px-4 py-2 text-sm font-bold uppercase tracking-wide text-[oklch(0.14_0.035_230)] transition-opacity hover:opacity-90"
            >
              Dashboard
            </Link>
          ) : (
            <>
              <Link
                to="/auth"
                className="text-sm font-semibold uppercase tracking-wide text-ice/90 transition-colors hover:text-cyan"
              >
                Sign in
              </Link>
              <Link
                to="/apply"
                className="rounded-sm bg-cyan px-4 py-2 text-sm font-bold uppercase tracking-wide text-[oklch(0.14_0.035_230)] transition-opacity hover:opacity-90"
              >
                Apply
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="-mr-2 inline-flex h-11 w-11 items-center justify-center text-ice lg:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open ? (
        <div className="border-t border-white/10 px-5 py-4 lg:hidden">
          <div className="flex flex-col gap-4">
            {NAV.map((item) => (
              <Link
                key={item.hash}
                to={item.to}
                hash={item.hash}
                onClick={() => {
                  setOpen(false);
                  if (window.location.pathname !== item.to) return;
                  // Wait for the panel to collapse so the header offset is
                  // measured at its final height before scrolling.
                  requestAnimationFrame(() =>
                    requestAnimationFrame(() => scrollToHash(item.hash)),
                  );
                }}

                data-active={activeSection === item.hash ? "true" : undefined}
              aria-current={activeSection === item.hash ? "location" : false}
                className={`flex items-center gap-3 border-l-2 pl-3 text-sm font-semibold uppercase tracking-wide transition-colors ${
                  activeSection === item.hash
                    ? "border-cyan text-cyan"
                    : "border-transparent text-ice/90"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/events"
              onClick={() => setOpen(false)}
              className="text-sm font-semibold uppercase tracking-wide text-ice/90"
            >
              Events
            </Link>
            <a
              href="https://t1dw.org"
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              className="text-sm font-semibold uppercase tracking-wide text-ice/90"
            >
              t1dw.org
            </a>

            {user ? (
              <Link
                to="/dashboard"
                onClick={() => setOpen(false)}
                className="text-sm font-bold uppercase text-cyan"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/auth"
                  onClick={() => setOpen(false)}
                  className="text-sm font-semibold uppercase text-ice/90"
                >
                  Sign in
                </Link>
                <Link
                  to="/apply"
                  onClick={() => setOpen(false)}
                  className="text-sm font-bold uppercase text-cyan"
                >
                  Apply to volunteer
                </Link>
              </>
            )}

          </div>
        </div>
      ) : null}
    </header>
  );
}
